export const FADE_TIMEOUT = 2000;
export const DRAWER_WIDTH = 240;
export const SIDEKICK_DASHBOARD_PATH = '/myStudySidekick';
export const FLASHCARD_INPUT_MAX_LENGTH = {
  NAME: 1000,
  DEFINITION: 2000,
  HINT: 1000,
};

/*
 * What each kind of study tool is called when one is named to the learner in
 * the singular — the ⓘ panel beside a heading, and the dashboard's menus,
 * table headings, dialogs and toasts, which take theirs from RESOURCE_TYPES.
 */
export const RESOURCE_LABEL = {
  FLASHCARD_DECK: 'Flashcard Deck',
  QUIZ: 'Practice Quiz',
  SUMMARY_NOTES: 'Summary Notes',
};

// How much of a written answer the feedback form takes, matching the backend.
export const FEEDBACK_DETAIL_MAX_LENGTH = 500;
/*
 * The faces that mean the learner is unhappy — the two at the bottom of the
 * scale in LikertFaceScale. Only these are asked what they would change.
 */
export const FEEDBACK_LOW_RATINGS = [0, 1];

export const AI_DISCLAIMER =
  'This study tool was generated with the assistance of myStudySidekick. The content was created to aid learning but may contain inaccuracies. Users are responsible for verifying critical facts and source material independently.';

// §3.3 of the TDD caps a generation request at 50 questions.
export const MAX_QUIZ_QUESTIONS = 50;

/*
 * Lifecycle of a generated resource. Quizzes and flashcard decks share it, and
 * it is mirrored by RESOURCE_STATUS in the backend's common constants.
 */
export const RESOURCE_STATUS = {
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
  ERROR: 'error',
};

export const RESOURCE_TYPE = {
  FLASHCARDS: 'flashcards',
};

// Answer keys a question's options dictionary may use, in display order.
export const QUIZ_OPTION_KEYS = ['A', 'B', 'C', 'D', 'E', 'F'];

/*
 * Answers this short sit two-per-row instead of stacking full width, which is
 * what makes True/False and one-word answers read as a compact grid.
 */
export const SHORT_ANSWER_LENGTH = 40;

/*
 * How much of a generated resource's name is shown wherever one is named: a
 * heading, a breadcrumb, a row in the table. Past this the name is cut short,
 * and the whole of it is told through the tooltip that goes with it.
 */
export const RESOURCE_TITLE_MAX_LENGTH = 50;

export const QUIZ_INSTRUCTIONS =
  'Complete each question by selecting an answer. Press “Submit Practice Quiz” at the end to submit your quiz. Question numbers will be marked as completed once an answer has been selected.';

// Said in the modal that opens when Submit is pressed, whichever way it goes.
export const QUIZ_UNANSWERED_MESSAGE =
  'All questions must be answered before submitting practice quiz.';

export const QUIZ_SUBMIT_CONFIRM = 'Are you ready to submit?';

export const QUIZ_EDITOR_INSTRUCTIONS =
  'Tap text to edit. Changes will not be saved until “Save” button is clicked.';

export const QUIZ_INCOMPLETE_ERROR = 'Fill in the highlighted fields before saving.';

/*
 * The two kinds of question the learner can add. Only the shape of the answers
 * differs: True/False comes with its answers written for it.
 */
export const QUIZ_QUESTION_TYPE = {
  TRUE_FALSE: 'trueFalse',
  MULTIPLE_CHOICE: 'multipleChoice',
};

// A True/False question's answers are fixed, and always in this order.
export const QUIZ_TRUE_FALSE_ANSWERS = ['True', 'False'];

// How many empty answers a new multiple choice question starts with: A–D.
export const QUIZ_NEW_ANSWER_COUNT = 4;

export const QUIZ_TRUE_FALSE_LOCKED = 'True or false answer texts are not editable.';

/*
 * Hints come from generation, so a question the learner wrote themselves has
 * none unless they added one. Said on the quiz itself, where the lightbulb is
 * disabled and the learner is owed a reason.
 */
export const QUIZ_MANUAL_QUESTION_NO_HINT = 'Question added manually without hint.';

// Backend error codes mapped to the messages displayed to the users.
export const RESOURCE_ERROR_MESSAGES = {
  RESOURCE_NOT_OWNED: (label) => `You don't have access to this ${label}.`,
  RESOURCE_WRONG_COURSE: (label) => `This ${label} isn't part of your currently selected course.`,
  INSUFFICIENT_CONTEXT: (label) =>
    `Not enough course content was available to generate this ${label}. Try selecting different or additional resources.`,
};

export const ALL_RESOURCES_TAB = 'all';

/*
 * DOMPurify settings for summary HTML, wherever it is shown, loaded into the
 * editor or saved. Summaries hold text only, so only text tags are allowed:
 * the ones the editor toolbar creates, plus quotes, code blocks, divs and
 * tables, which saved summaries (e.g. AI output) can contain. Every other tag
 * is removed with its text kept, e.g. forms, buttons and img; video, audio,
 * svg and <style> blocks are removed along with their content.
 * Only href and style attributes are kept: no class or id (which could reuse
 * the app's own CSS), and no data-* or aria-* (which DOMPurify would otherwise
 * keep by default). DOMPurify still removes scripts, event handlers such as
 * onerror, and javascript: links. The backend applies the same settings before
 * saving (SANITIZE_CONFIG in backend/config/constants/common.js); keep both in
 * sync.
 */
export const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'strong',
    'b',
    'em',
    'i',
    'u',
    's',
    'strike',
    'span',
    'a',
    'ul',
    'ol',
    'li',
    'div',
    'blockquote',
    'pre',
    'table',
    'thead',
    'tbody',
    'tfoot',
    'tr',
    'th',
    'td',
  ],
  ALLOWED_ATTR: ['href', 'style'],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
  // Links keep their href only when it is an https:// address. Any other
  // address (http:, mailto:, tel:, relative paths, ...) is removed and the
  // link text stays as plain text.
  ALLOWED_URI_REGEXP: /^https:\/\//i,
};

/*
 * The only inline styles kept in summary HTML: the ones the editor toolbar
 * creates (text color, highlight, alignment). Every other style is removed,
 * since styles such as position, margin or transform can draw an element over
 * the rest of the app, e.g. a fake "session expired" screen. Used by
 * sanitizeSummaryHtml and by the paste cleaning (sanitizePastedHtml). The backend keeps
 * the same list (SANITIZE_ALLOWED_STYLES in backend/config/constants/common.js);
 * keep both in sync.
 */
export const SANITIZE_ALLOWED_STYLES = ['color', 'background-color', 'text-align'];
